import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Icon, Badge, Button, Watermark } from '../../components/ui';
import { EXTERNAL_URLS } from '../../constants/urls';

const AARTI_SCHEDULE = [
  { name: 'Mangal Aarti', time: '4:00 AM', duration: '30 mins', desc: 'The earliest and most auspicious aarti, performed at dawn to awaken the deity.' },
  { name: 'Shringar Aarti', time: '6:00 AM', duration: '30 mins', desc: 'Adorning aarti where the deity is beautifully dressed and ornamented.' },
  { name: 'Bhog Aarti', time: '12:00 PM', duration: '20 mins', desc: 'Midday offering of bhog to Shri Ram Lalla.' },
  { name: 'Shayan Aarti', time: '10:00 PM', duration: '30 mins', desc: 'The final aarti of the day, performed to put the deity to rest.' },
];

const SARYU_AARTI = [
  { name: 'Saryu Aarti at Ram ki Paidi', time: 'At sunset', duration: '45 mins', desc: 'Riverside aarti on the ghats — open to all, no pass required.' },
];

const GUIDELINES = [
  { icon: 'clock', title: 'Arrive early', desc: 'Reach 30 minutes before the scheduled aarti time.' },
  { icon: 'ticket', title: 'Aarti pass', desc: 'A free pass from the Trust portal is required for aarti inside the Mandir.' },
  { icon: 'camera', title: 'No photography', desc: 'Cameras and phones are not allowed during the ceremony.' },
  { icon: 'video', title: 'Watch from home', desc: 'Live streaming is available for devotees who cannot travel.' },
];

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Event',
  name: 'Daily Aarti at Shri Ram Janmabhoomi Mandir',
  description: 'Mangal, Shringar, Bhog and Shayan aarti performed daily at the Ram Mandir, Ayodhya.',
  eventSchedule: AARTI_SCHEDULE.map((a) => ({ '@type': 'Schedule', name: a.name, startTime: a.time, repeatFrequency: 'P1D' })),
  location: { '@type': 'Place', name: 'Shri Ram Janmabhoomi Mandir', address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' } },
  eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
  isAccessibleForFree: true,
};

function ScheduleCard({ title, items, t }) {
  return (
    <div className="card card--pad">
      <h2 className="h-card" style={{ marginBottom: 14 }}>{title}</h2>
      <ul className="info-list">
        {items.map((a) => (
          <li key={a.name}>
            <span className="info-list__icon"><Icon name="aarti" size={16} /></span>
            <div>
              <div className="aarti-row">
                <span className="info-list__value">{t(a.name)}</span>
                <span className="aarti-row__time">{t(a.time)}</span>
              </div>
              <div className="info-list__label" style={{ marginTop: 2 }}>{t(a.desc)} · {t(a.duration)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Aarti() {
  const { t } = useTranslation();

  return (
    <>
      <SEO
        title="Live Darshan & Aarti Timings"
        description="Watch Ram Mandir aarti live or plan your in-person darshan. Mangal Aarti 4 AM, Shringar 6 AM, Bhog 12 PM, Shayan 10 PM, plus the Saryu aarti at sunset."
        canonicalUrl="/aarti"
        keywords="Ram Mandir aarti, Mangal aarti Ayodhya, aarti timing Ram Mandir, live aarti, Saryu aarti"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Booking') }, { label: t('Aarti Passes') }]} title={t('Live Darshan & Aarti')} subtitle={t('Join the daily aartis of Shri Ram Lalla from anywhere')} />

      <div className="container content-wrap">
        <div className="card aarti-live">
          <div className="gallery__main aarti-live__media">
            <img src={EXTERNAL_URLS.RAM_MANDIR.LIVE_AARTI_THUMBNAIL} alt={t('Ram Mandir aarti live stream')} />
            <div className="aarti-live__overlay">
              <Badge variant="live">LIVE</Badge>
              <div className="aarti-live__title">{t('Ram Lalla Live Darshan')}</div>
              <p className="aarti-live__sub">{t('Join millions of devotees in the daily aarti, streamed from the temple.')}</p>
              <Button variant="white" href={EXTERNAL_URLS.RAM_MANDIR.LIVE_AARTI} icon={<Icon name="circle-play" size={18} />} iconPosition="left">
                {t('Watch Live')}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ marginTop: 28 }}>
          <ScheduleCard title={t('Aarti at Ram Mandir')} items={AARTI_SCHEDULE} t={t} />
          <div className="vstack" style={{ gap: 20 }}>
            <ScheduleCard title={t('Aarti on the Saryu')} items={SARYU_AARTI} t={t} />
            <div className="card card--pad">
              <h2 className="h-card" style={{ marginBottom: 14 }}>{t('Before you attend')}</h2>
              <ul className="feature-list">
                {GUIDELINES.map((g) => (
                  <li key={g.title}>
                    <span className="feature-list__icon"><Icon name={g.icon} size={18} /></span>
                    <div>
                      <div className="feature-list__title">{t(g.title)}</div>
                      <div className="feature-list__body">{t(g.desc)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="cta-band" style={{ marginTop: 32 }}>
          <Watermark variant="mandala" position="top-left" />
          <Watermark variant="mandala" position="bottom-right" />
          <h2 className="cta-band__title">{t('Planning to attend in person?')}</h2>
          <p className="cta-band__sub">{t('Aarti and darshan passes for the Ram Mandir are free and issued by the Trust. Start here and we will guide you.')}</p>
          <div className="cta-band__actions">
            <Button to="/darshan-booking" icon={<Icon name="temple" size={16} />} iconPosition="left">{t('Darshan Booking')}</Button>
            <Button variant="outline" to="/temple-timings" icon={<Icon name="clock" size={16} />} iconPosition="left">{t('Temple Timings')}</Button>
          </div>
        </div>
      </div>
    </>
  );
}
